<template>
  <side-extended-panel id="gift-chart"
                       :close-btn-text="$t('owl.close')"
                       :visible="show"
                       size="x-small"
                       extra-container-classes="p-0"
                       @close="() => $store.commit('ui/CLOSE_SIDE_SHEET')">

    <template #header>
      <new-header :title="$t('gifts.title')" icon="icon-gift"/>
    </template>
    <div class="px-4">
      <div class="mb-4">
        <p v-if="!showMore">{{ $t(`gifts.${personal ? 'personal' : 'connection'}.paragraph`) }}</p>
        <div v-else v-html="$t(`gifts.${personal ? 'personal' : 'connection'}.extended`)"/>
        <b-button class="btn-primary-link" variant="link" @click="showMore = !showMore">
          {{ showMore ? $t('general.close') : $t('general.showMore') }}
        </b-button>
      </div>

      <div v-for="(item, index) in boxes" :key="item.type" :title="item.title" class="card mb-3">
        <div class="card-body">
          <div class="card-title"><h5>{{item.title}}</h5></div>

          <div v-if="item.isGift">
            <p class="mb-1"><span class="font-500">Gift Received</span> — {{$t('gifts.received.text')}} </p>
          </div>
          <div v-else-if="!item.isPurchased">
            <div class="gift-text-xs" v-html="item.text" />

            <div class="d-flex justify-content-between align-items-center">
              <b-button variant="primary" size="xs" @click="unlock(item)">{{$t('billing.unlock')}} {{$t('billing.unlockTypes.'+item.type)}}</b-button>
              <p v-if="item.product" class="gift-text-xs mb-0">${{item.product.price}}</p>
            </div>
          </div>
          <div v-else-if="item.isGiftClaimed">
            <p class="mb-0">
              {{$t('gifts.received.thanks', { name: item.isGiftClaimed.name }) }}
            </p>
          </div>
          <div v-else>
            <div v-html="$t(`gifts.${personal ? 'personal' : 'connection'}.link`)" class="gift-text-xs"/>

            <div class="d-flex justify-content-between align-items-center mt-3">
              <b-button variant="outline-white" size="xs" class="" :disabled="item.isFreePurchase" @click="copyLink(item, index)">
                <span v-if="copied[index]">
                  <span class="icon-check"/> {{$t('general.copied')}}
                </span>
                  <span v-else>
                  {{$t('gifts.copyLink')}}
                </span>
              </b-button>

              <div class=" text-sm mb-0 color-green"> {{$t('gifts.purchased')}}</div>
            </div>

          </div>
        </div>

      </div>
    </div>
  </side-extended-panel>

</template>

<style lang="scss">

#share-input{
  input{
    opacity:1!important;
  }
}
</style>

<script>

import SideExtendedPanel from "../SideExtendedPanel.vue";
import {mapState} from "vuex";

import ChartUtil from "@/mixins/ChartUtil";
import {createSingleCartItem} from "@/helpers/checkout_helper";
import GiftMixin from "@/mixins/GiftMixin";
import NewHeader from "@/components/structure/chart-sidebars/partials/NewHeader.vue";
import {SidebarVariants} from "@/utils/localStorage";
import ServerContentMixin from "@/mixins/ServerContentMixin";

export default{
  components: {NewHeader, SideExtendedPanel },
  mixins: [ChartUtil, GiftMixin, ServerContentMixin],
  props:{
    personal: Boolean,
    chartId: String,
    name: String,
  },
  computed:{
    ...mapState({
      products: state => state.checkout.products
    }),

    show(){
      return this.$store.state.ui.sideSheet.type === SidebarVariants.GiftChart;
    },

    purchaseIds(){
      return this.unlockedCharts.filter(x => x.chart_id === this.chartId).map(x => x.userSinglePurchaseId).filter(x => !!x);
    },

    getGiftStatus() {
      let unlocks = this.unlockedCharts.filter(x => x.chart_id === this.chartId);
      return {
        foundational: unlocks.find(x => x.purchase_type === 'foundational'),
        advanced: unlocks.find(x => x.purchase_type === 'advanced'),
        companionship: unlocks.find(x => x.purchase_type === 'companionship'),
      }
    },

    boxes() {
      // we check the status of the chart.
      const status = this.getUnlockStatus({id: this.chartId});
      const giftStatus = this.getGiftStatus;

      for (const key of Object.values(giftStatus)) {
        if (!key) continue;
        key.purchase = this.purchases.find(x => x.id === key.userSinglePurchaseId);

        const cartItem = key.purchase?.metadata?.cart?.find(x => x.chart_id === this.chartId);
        key.isGiftClaimed = cartItem?.giftedChartId ? cartItem.recipient: null;
      }

      if (!this.personal) {
        return [{
          type: 'companionship',
          unlockCode: '2',
          title: this.$t('billing.products.names.companionship'),
          text: this.getByKey('checkout.single.companionship.short'),
          isGift: !!giftStatus.companionship?.metadata?.isGift,
          isPurchased: status.companionship,
          product: this.products.singles.find(x => x.key === 'companionship'),
          isFreePurchase: !giftStatus.companionship?.purchase || giftStatus.companionship?.purchase.provider === 2,
          purchase: giftStatus.companionship?.purchase,
          isGiftClaimed: giftStatus.companionship?.isGiftClaimed,
          chartId: this.chartId
        }]
      }

      const personalGifts = [
        {
          type: 'foundational',
          unlockCode: '0',
          title: this.$t('billing.products.names.foundational'),
          text: this.getByKey('checkout.single.foundational.short'),
          isGift: !!giftStatus.foundational?.metadata?.isGift,
          isPurchased: status.foundational,
          product: this.products.singles.find(x => x.key === 'foundational'),
          isFreePurchase: !giftStatus.foundational?.purchase || giftStatus.foundational?.purchase.provider === 2,
          purchase: giftStatus.foundational?.purchase,
          isGiftClaimed: giftStatus.foundational?.isGiftClaimed,
          chartId: this.chartId
        },
        {
          type: 'advanced',
          unlockCode: '01',
          title: this.$t('billing.products.names.advanced'),
          text: this.getByKey('checkout.single.advanced.short'),
          isGift: !!giftStatus.advanced?.metadata?.isGift,
          isPurchased: status.advanced,
          product: this.products.singles.find(x => x.key === 'advanced'),
          isFreePurchase: !giftStatus.advanced?.purchase || giftStatus.advanced?.purchase.provider === 2,
          purchase: giftStatus.advanced?.purchase,
          isGiftClaimed: giftStatus.advanced?.isGiftClaimed,
          chartId: this.chartId
        },
      ];
      personalGifts.push({
        type: 'work_success',
        unlockCode: 'w2',
        title: this.$t('billing.products.names.work_success'),
        text: this.getByKey('checkout.single.work_success.short'),
        isGift: !!giftStatus.work_success?.metadata?.isGift,
        isPurchased: status.work_success,
        product: this.products.singles.find(x => x.key === 'work_success'),
        isFreePurchase: !giftStatus.work_success?.purchase || giftStatus.work_success?.purchase.provider === 2,
        purchase: giftStatus.work_success?.purchase,
        isGiftClaimed: giftStatus.work_success?.isGiftClaimed,
        chartId: this.chartId
      });

      return personalGifts;
    }
  },
  watch: {
    purchaseIds(){
      this.getPurchases()
    }
  },
  mounted(){
    this.getPurchases()
  },
  data(){
    return{
      showMore: false,
      purchases: [],
      loading: false,
    }
  },
  methods:{
    getPurchases(){
      if (this.purchaseIds.length === 0) return;
      if (this.loading) return;
      this.loading = true;
      this.axios.get('/billing/gifts/purchases', { params: {
        purchaseIds: this.purchaseIds
      }} ).then(res =>{
        this.purchases = res.data;
      }).catch(() => {
      }).finally(() => { this.loading = false });
    },
    unlock(item){
      this.$store.commit('checkout/setCheckoutType', this.CheckoutTypes.SINGLE);
      this.$store.commit('checkout/addSingleCartItem', createSingleCartItem({
        id: this.chartId,
        name: this.name
      }, item.product));
      this.$router.push({name: 'checkout'});
    },
  }

}
</script>